<template>
  <a-layout class="hospital-management">
    <a-layout-content>
      <a-typography-title :level="3">병원 관리</a-typography-title>
      
      <!-- 검색 및 필터 영역 -->
      <HosCrawlFilter
        :selectedSource="filters.source"
        :selectedCity="filters.city"
        :selectedTreatment="filters.treatmentId"
        :searchKeyword="filters.keyword"
        @update:selectedSource="onSourceChange"
        @update:selectedCity="onCityChange"
        @update:selectedTreatment="onTreatmentChange"
        @update:searchKeyword="onKeywordChange"
        @apply-filters="fetchHospitals"
        @reset-filters="resetFilters"
      />
      
      <!-- 디버그 정보 (개발 중에만 사용) -->
      <div v-if="debug" style="margin-bottom: 16px; padding: 8px; background: #eaeaea; border-radius: 4px;">
        <p>데이터 디버그:</p>
        <pre style="max-height: 200px; overflow: auto; font-size: 10px;">{{ JSON.stringify(hospitals[0], null, 2) }}</pre>
      </div>
      
      <!-- 데이터 테이블 -->
      <a-card :bordered="false" style="margin-top: 16px" class="data-table-card">
        <template #title>
          <a-row justify="space-between" align="middle">
            <a-col>
              <span style="color: #666">전체 {{ totalItems }}건</span>
            </a-col>
            <a-col>
              <a-space>
                <a-switch v-model:checked="debug" size="small" />
                <span style="font-size: 12px">디버그</span>
                <a-select
                  v-model:value="pageSize"
                  style="width: 140px"
                  @change="handlePageSizeChange"
                >
                  <a-select-option :value="10">10개씩 보기</a-select-option>
                  <a-select-option :value="20">20개씩 보기</a-select-option>
                  <a-select-option :value="50">50개씩 보기</a-select-option>
                </a-select>
              </a-space>
            </a-col>
          </a-row>
        </template>
        
        <a-table
          :dataSource="hospitals"
          :columns="columns"
          :pagination="pagination"
          :loading="loading"
          @change="handleTableChange"
          :scroll="{ x: 1400 }"
          rowKey="_id"
          :bordered="true"
          size="middle"
          :locale="{emptyText: '데이터가 없습니다.'}"
        >
          <!-- 테이블 열 커스텀 렌더링 -->
          <template #bodyCell="{ column, record, index }">
            <!-- 번호 열 -->
            <template v-if="column.key === 'index'">
              <div class="row-number">{{ getRowIndex(index) }}</div>
            </template>
            
            <!-- 이름 열 -->
            <template v-else-if="column.dataIndex === 'hospital_name'">
              <span class="cell-content">{{ record.hospital_name || record.name || '-' }}</span>
            </template>
            
            <!-- 진료과목 열 -->
            <template v-else-if="column.dataIndex === 'department'">
              <span class="cell-content">{{ record.department || '-' }}</span>
            </template>
            
            <!-- 카테고리 열 -->
            <template v-else-if="column.dataIndex === 'category'">
              <span class="cell-content">{{ record.category || '-' }}</span>
            </template>
            
            <!-- 점수/별점 열 -->
            <template v-else-if="column.dataIndex === 'score'">
              <span class="cell-content">
                <template v-if="record.score">
                  <a-rate :value="getStarRating(record.score)" :count="5" disabled :allowHalf="true" style="font-size: 14px; margin-right: 4px;" />
                  <span>{{ Number(record.score).toFixed(1) }}</span>
                </template>
                <template v-else>
                  <span>평점 없음</span>
                </template>
              </span>
            </template>
            
            <!-- 원본사이트 열 -->
            <template v-else-if="column.dataIndex === 'source'">
              <span class="cell-content">
                <a-tag :color="getSourceColor(record.source)">
                  {{ formatSourceName(record.source) }}
                </a-tag>
              </span>
            </template>
            
            <!-- 이미지 열 -->
            <template v-else-if="column.dataIndex === 'image_url'">
              <div class="image-cell-wrapper">
                <a-image
                  :src="getHospitalImageUrl(record)"
                  :width="100"
                  :height="100"
                  :fallback="fallbackImg"
                  :preview="{
                    src: getHospitalImageUrl(record),
                    toolbars: {
                      download: true,
                      fullScreen: true,
                      rotateRight: true,
                      rotateLeft: true,
                      flipHorizontal: true,
                      flipVertical: true
                    },
                    title: record.hospital_name || record.name || '병원 이미지'
                  }"
                  class="hospital-image"
                />
              </div>
            </template>
            
            <!-- 시술 정보 열 -->
            <template v-else-if="column.dataIndex === 'treatments'">
              <div v-if="hasTreatments(record)" class="treatments-container">
                <div v-for="treatment in getTreatments(record)" :key="treatment._id || treatment.treatment_groups_id" class="treatment-item">
                  <div class="treatment-header">
                    <span class="treatment-name">{{ treatment.treatment_groups_name }}</span>
                    <span class="treatment-counts">
                      <a-tooltip title="시술을 확인한 사람수">
                        <span class="count-item">
                          <eye-outlined /> {{ treatment.view_count || 0 }}
                        </span>
                      </a-tooltip>
                      <a-tooltip title="시술을 예약한 사람수">
                        <span class="count-item">
                          <check-outlined /> {{ treatment.submit_count || 0 }}
                        </span>
                      </a-tooltip>
                    </span>
                  </div>
                  <div class="treatment-category">
                    <a-tag color="purple">진료과목: {{ getTreatmentDepartment(treatment) }}</a-tag>
                    <a-tag color="blue">카테고리: {{ getTreatmentCategory(treatment) }}</a-tag>
                  </div>
                  <div class="treatment-prices">
                    <a-tag color="blue">가격: {{ formatPrice(treatment.min_normal_price, treatment.max_normal_price) }}</a-tag>
                    <a-tag v-if="hasEventPrice(treatment)" color="green">행사가: {{ formatPrice(treatment.min_event_price, treatment.max_event_price) }}</a-tag>
                  </div>
                  <div v-if="treatment.image_url" class="treatment-image">
                    <a-image
                      :src="treatment.image_url"
                      :width="60"
                      :height="60"
                      :fallback="fallbackImg"
                      :preview="{
                        src: treatment.image_url,
                        toolbars: {
                          download: true,
                          fullScreen: true,
                          rotateRight: true,
                          rotateLeft: true
                        },
                        title: `${treatment.treatment_groups_name || '시술'} 이미지`
                      }"
                    />
                  </div>
                </div>
                <a-button 
                  v-if="record.treatments && record.treatments.length > 3"
                  type="link"
                  size="small"
                  @click="showMoreTreatments(record)"
                  style="padding-left: 0"
                >
                  +{{ record.treatments.length - 3 }}개 더보기
                </a-button>
              </div>
              <a-empty v-else description="등록된 시술 정보가 없습니다." :image-style="{ height: '40px' }" />
            </template>
            
            <!-- 원본링크 열 -->
            <template v-else-if="column.dataIndex === 'actions'">
              <a-space>
                <a-button 
                  v-if="canGenerateOriginalUrl(record)" 
                  type="primary"
                  ghost
                  size="small"
                  :href="generateOriginalUrl(record)" 
                  target="_blank"
                  style="display: flex; align-items: center; gap: 4px;"
                >
                  <template #icon><link-outlined /></template>
                  바로가기
                </a-button>
                <span v-else class="no-link">링크 없음</span>
              </a-space>
            </template>
          </template>
        </a-table>
      </a-card>
    </a-layout-content>
  </a-layout>
</template>

<script>
import { defineComponent, ref, reactive, computed, onMounted, watch } from 'vue';
import { EyeOutlined, CheckOutlined, LinkOutlined } from '@ant-design/icons-vue';
import HosCrawlFilter from './components/HosCrawlFilter.vue';
import TreatmentCard from './components/TreatmentCard.vue';
import { HospitalCrawlingApi } from './api.js';

export default defineComponent({
  name: "hosCrawlManage",
  components: {
    HosCrawlFilter,
    TreatmentCard,
    EyeOutlined,
    CheckOutlined,
    LinkOutlined
  },
  setup() {
    // 상태 정의
    const hospitals = ref([]);
    const loading = ref(false);
    const currentPage = ref(1);
    const pageSize = ref(10);
    const totalItems = ref(0);
    const fallbackImg = '/assets/img/no-image.png'; // 기본 이미지 경로로 변경
    const debug = ref(false); // 디버그 모드
    const debounceTimer = ref(null); // 디바운스 타이머

    // 필터 상태
    const filters = reactive({
      source: '',
      city: '',
      treatmentId: '',
      keyword: ''
    });

    // 디바운스 검색 실행 함수
    const debouncedSearch = (delay = 500) => {
      if (debounceTimer.value) {
        clearTimeout(debounceTimer.value);
      }
      
      debounceTimer.value = setTimeout(() => {
        console.log('디바운스 검색 실행');
        currentPage.value = 1; // 페이지 초기화
        fetchHospitals();
      }, delay);
    };

    // 드롭다운 필터 값 변경 감지 (소스, 지역, 치료항목)
    watch(() => [filters.source, filters.city, filters.treatmentId], 
      ([newSource, newCity, newTreatment], [oldSource, oldCity, oldTreatment]) => {
        console.log('드롭다운 필터 값이 변경되었습니다:', { 
          source: { old: oldSource, new: newSource },
          city: { old: oldCity, new: newCity },
          treatment: { old: oldTreatment, new: newTreatment }
        });
        
        // 이전 값과 현재 값이 다른 경우에만 API 호출
        if (newSource !== oldSource || newCity !== oldCity || newTreatment !== oldTreatment) {
          // 값이 실제로 바뀐 경우에만 호출 (초기 undefined → '' 변경은 무시)
          if ((oldSource !== undefined && newSource !== oldSource) || 
              (oldCity !== undefined && newCity !== oldCity) || 
              (oldTreatment !== undefined && newTreatment !== oldTreatment)) {
            console.log('드롭다운 필터 변경으로 인한 즉시 검색 실행');
            currentPage.value = 1; // 페이지 초기화
            fetchHospitals();
          }
        }
      }, 
      { deep: true }
    );
    
    // 키워드 필터 값 변경 감지
    watch(() => filters.keyword, (newKeyword, oldKeyword) => {
      if (newKeyword !== oldKeyword && oldKeyword !== undefined) {
        console.log('키워드 변경 감지:', { old: oldKeyword, new: newKeyword });
        debouncedSearch(500); // 0.5초 지연 후 검색 실행
      }
    });

    // 테이블 컬럼 설정
    const columns = [
      {
        title: '번호',
        key: 'index',
        width: 70,
        fixed: 'left',
        align: 'center'
      },
      {
        title: '이름',
        dataIndex: 'hospital_name',
        key: 'hospital_name',
        width: 180,
        fixed: 'left',
        align: 'center'
      },
      {
        title: '주소',
        dataIndex: 'address',
        key: 'address',
        width: 250,
        align: 'center'
      },
      {
        title: '진료과목',
        dataIndex: 'department',
        key: 'department',
        width: 120,
        align: 'center'
      },
      {
        title: '카테고리',
        dataIndex: 'category',
        key: 'category',
        width: 120,
        align: 'center'
      },
      {
        title: '평점',
        dataIndex: 'score',
        key: 'score',
        width: 80,
        align: 'center'
      },
      {
        title: '원본사이트',
        dataIndex: 'source',
        key: 'source',
        width: 100,
        align: 'center'
      },
      {
        title: '이미지',
        dataIndex: 'image_url',
        key: 'image_url',
        width: 150,
        align: 'center'
      },
      {
        title: '시술정보',
        dataIndex: 'treatments',
        key: 'treatments',
        width: 280,
        align: 'center'
      },
      {
        title: '원본링크',
        dataIndex: 'actions',
        key: 'actions',
        width: 100,
        fixed: 'right',
        align: 'center'
      }
    ];

    // 페이지네이션 설정
    const pagination = computed(() => ({
      total: totalItems.value,
      current: currentPage.value,
      pageSize: pageSize.value,
      showSizeChanger: true, // 페이지 크기 변경 옵션 활성화
      pageSizeOptions: ['10', '20', '50', '100'], // 페이지 크기 옵션
      showTotal: (total) => `총 ${total}건`,
      onChange: (page) => {
        currentPage.value = page;
        fetchHospitals();
      },
      onShowSizeChange: (current, size) => {
        pageSize.value = size;
        currentPage.value = 1; // 페이지 크기 변경 시 첫 페이지로 이동
        fetchHospitals();
      }
    }));

    // 병원 데이터 조회
    const fetchHospitals = async () => {
      loading.value = true;
      try {
        // 필터 데이터 디버깅
        if (debug.value) {
          console.log('현재 필터 상태:', { ...filters });
        }
        
        const params = {
          page: currentPage.value,
          limit: pageSize.value,
          ...filters
        };
        
        // 필터가 비어있는 경우 로그 출력
        if (!filters.source && !filters.city && !filters.treatmentId && !filters.keyword) {
          console.log('모든 필터가 비어 있습니다. 전체 데이터를 가져옵니다.');
        }
        
        const response = await HospitalCrawlingApi.getHospitals(params);
        console.log('API 응답:', response); // 응답 데이터 확인
        
        // API 응답 구조에 따라 데이터 할당 방식 수정
        const rawData = response.items || response.data?.items || response.data || [];
        console.log('원본 데이터 구조:', rawData);
        
        // API 응답이 배열인지 확인
        const items = Array.isArray(rawData) 
          ? rawData 
          : Array.isArray(rawData.result) 
            ? rawData.result 
            : [];
        
        // 데이터 매핑 및 변환
        hospitals.value = items.map(item => {
          // 디버깅 - 항목 데이터 확인
          if (debug.value) {
            console.log('항목 데이터:', {
              id: item._id,
              name: item.hospital_name || item.name,
              department: item.department,
              category: item.category,
              score: item.score,
              total_score: item.total_score,
              has_image: !!item.image_url,
              treatments_count: item.treatments?.length
            });
          }
          
          // 첫 번째 시술에서 진료과목과 카테고리 추출
          let department = item.department || '-';
          let category = item.category || '-';
          
          // 시술 정보가 있는 경우 첫 번째 시술에서 진료과목과 카테고리 추출
          if (item.treatments && item.treatments.length > 0 && item.treatments[0].treatment_group_s) {
            const firstTreatment = item.treatments[0];
            
            // 가장 내부 그룹(진료과목) 추출
            if (firstTreatment.treatment_group_s && 
                firstTreatment.treatment_group_s.group && 
                firstTreatment.treatment_group_s.group.group && 
                firstTreatment.treatment_group_s.group.group.name) {
              department = firstTreatment.treatment_group_s.group.group.name;
            }
            
            // 중간 그룹(카테고리) 추출
            if (firstTreatment.treatment_group_s && 
                firstTreatment.treatment_group_s.group && 
                firstTreatment.treatment_group_s.group.name) {
              category = firstTreatment.treatment_group_s.group.name;
            }
          }
          
          // 기존 데이터는 그대로 유지하면서 필요한 필드만 변환
          return {
            ...item,
            // 데이터 일관성을 위한 추가 처리
            _id: item._id || `temp-${Math.random()}`, // rowKey를 위한 고유 ID 보장
            hospital_name: item.hospital_name || item.name || '-',
            department: department, // 추출한 진료과목 설정
            category: category, // 추출한 카테고리 설정
            score: item.total_score || item.score || 0, // total_score 우선 사용
            image_url: item.image_url || '',
            // 데이터 표시 형식 조정
            treatments: Array.isArray(item.treatments) 
              ? item.treatments.map(t => ({
                  ...t,
                  // treatment_group_s에서 필요한 정보 추출
                  treatment_groups_id: t.treatment_groups_id || t.treatment_group_s?.id,
                  treatment_groups_name: t.treatment_groups_name || t.treatment_group_s?.name,
                  // 진료과목과 카테고리 정보 추가
                  department: t.treatment_group_s?.group?.group?.name || '-',
                  category: t.treatment_group_s?.group?.name || '-'
                }))
              : []
          };
        });
        
        // totalItems 값 설정 (백엔드 응답에서 total 값 사용)
        totalItems.value = response.total || response.data?.total || items.length || 0;
        
        // 디버깅용 로그
        console.log('처리된 병원 데이터:', {
          total: totalItems.value,
          page: currentPage.value,
          limit: pageSize.value,
          items: hospitals.value.length
        });
        
        // 데이터가 비어있는 경우
        if (hospitals.value.length === 0) {
          console.log('검색 결과가 없습니다.');
        }
        
      } catch (error) {
        console.error('병원 정보 조회 오류:', error);
        // 에러 시 빈 배열로 초기화
        hospitals.value = [];
        totalItems.value = 0;
      } finally {
        loading.value = false;
      }
    };

    // 필터 초기화
    const resetFilters = () => {
      // 모든 필터 필드를 초기화
      Object.keys(filters).forEach(key => {
        filters[key] = '';
      });
      
      // 페이지 초기화
      currentPage.value = 1;
      
      console.log('필터가 초기화되었습니다.');
      
      // 데이터 다시 조회
      fetchHospitals();
    };

    // 페이지 크기 변경
    const handlePageSizeChange = (value) => {
      console.log('페이지 크기 변경:', value);
      pageSize.value = value;
      currentPage.value = 1; // 페이지 크기 변경 시 첫 페이지로 이동
      fetchHospitals();
    };

    // 테이블 변경 처리
    const handleTableChange = (paginationInfo, filters, sorter) => {
      console.log('테이블 변경 이벤트:', {
        pagination: paginationInfo,
        filters,
        sorter
      });
      
      // 페이지 정보 업데이트
      if (paginationInfo) {
        // 페이지 번호 변경
        if (paginationInfo.current !== currentPage.value) {
          currentPage.value = paginationInfo.current;
        }
        
        // 페이지 크기 변경
        if (paginationInfo.pageSize !== pageSize.value) {
          pageSize.value = paginationInfo.pageSize;
          currentPage.value = 1; // 페이지 크기 변경 시 첫 페이지로 이동
        }
      }
      
      // 새 데이터 조회
      fetchHospitals();
    };

    // 병원 이미지 URL 가져오기
    const getHospitalImageUrl = (hospital) => {
      // 1. 병원 자체 이미지가 있는 경우 사용
      let url = hospital.image_url || '';
      
      // 2. 없는 경우 첫 번째 시술 이미지 사용
      if (!url && hospital.treatments && hospital.treatments.length > 0) {
        // 시술 목록에서 이미지 URL이 있는 첫 번째 시술 찾기
        for (const treatment of hospital.treatments) {
          if (treatment.image_url) {
            url = treatment.image_url;
            break;
          }
        }
      }
      
      // URL이 유효한지 검사
      if (!url) return fallbackImg;
      
      // URL이 상대 경로인지 확인
      if (url.startsWith('/') && !url.startsWith('//')) {
        // API 서버 기본 URL 추가 (환경에 맞게 수정)
        return import.meta.env.VITE_API_URL 
          ? `${import.meta.env.VITE_API_URL}${url}` 
          : url;
      }
      
      return url;
    };

    // 시술 정보 확인
    const hasTreatments = (hospital) => {
      return hospital.treatments && hospital.treatments.length > 0;
    };

    // 시술 정보 가져오기 (최대 3개까지만 표시)
    const getTreatments = (hospital) => {
      if (!hasTreatments(hospital)) return [];
      
      // treatments 데이터 정렬 - view_count 기준 내림차순
      const sortedTreatments = [...hospital.treatments]
        .sort((a, b) => (b.view_count || 0) - (a.view_count || 0));
      
      return sortedTreatments.slice(0, 3);
    };

    // 이벤트 가격 유무 확인
    const hasEventPrice = (treatment) => {
      return treatment.min_event_price !== null && treatment.min_event_price !== undefined;
    };

    // 가격 포맷팅
    const formatPrice = (min, max) => {
      if (min === undefined || min === null) return '0원';
      
      if (min === max || max === undefined || max === null) {
        return addCommas(min) + '원';
      }
      return `${addCommas(min)}~${addCommas(max)}원`;
    };

    // 천 단위 쉼표 추가
    const addCommas = (num) => {
      if (num === null || num === undefined) return '0';
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    // 원본 URL 생성 가능 여부 확인
    const canGenerateOriginalUrl = (hospital) => {
      // 모두닥인 경우 hospital_id 필수
      if (hospital.source === 'modoodoc') {
        return !!hospital.hospital_id;
      }
      
      // 원본 URL이 직접 저장된 경우
      if (hospital.original_url) {
        return true;
      }
      
      // 추후 다른 소스별 조건 추가 가능
      return false;
    };

    // 원본 URL 생성
    const generateOriginalUrl = (hospital) => {
      // 디버깅: 병원 정보 로깅
      if (debug.value) {
        console.log('원본링크 생성 정보:', {
          hospital_id: hospital.hospital_id,
          hospital_name: hospital.hospital_name,
          source: hospital.source,
          has_treatments: hospital.treatments?.length > 0,
          original_url: hospital.original_url
        });
      }
      
      // 이미 원본 URL이 있는 경우
      if (hospital.original_url) {
        return hospital.original_url;
      }
      
      // 소스별 URL 생성 로직
      switch (hospital.source) {
        case 'modoodoc':
          return generateModoodocUrl(hospital);
        // 추후 다른 소스 추가 가능
        // case 'docple':
        //   return generateDocpleUrl(hospital);
        default:
          console.log('지원되지 않는 소스:', hospital.source);
          return '';
      }
    };
    
    // 모두닥 URL 생성
    const generateModoodocUrl = (hospital) => {
      if (!hospital.hospital_id) {
        console.log('hospital_id 없음', hospital);
        return '';
      }
      
      // 기본 URL 패턴: https://www.modoodoc.com/treatment-detail/{병원ID}/{시술ID}/?category=platform
      let url = `https://www.modoodoc.com/treatment-detail/${hospital.hospital_id}`;
      
      // 첫 번째 시술 ID가 있으면 추가
      let treatmentId = '10'; // 기본값 10 (임플란트 단일)
      
      if (hospital.treatments && hospital.treatments.length > 0) {
        // 시술 정보 있는 경우
        const firstTreatment = hospital.treatments[0];
        if (firstTreatment.treatment_groups_id) {
          treatmentId = firstTreatment.treatment_groups_id;
        } else if (firstTreatment.treatment_group_s?.id) {
          treatmentId = firstTreatment.treatment_group_s.id;
        }
      }
      
      url += `/${treatmentId}`;
      
      // 카테고리 파라미터 추가
      url += '/?category=platform';
      
      if (debug.value) {
        console.log('생성된 모두닥 URL:', url);
      }
      
      return url;
    };

    // 모든 시술 정보 보기
    const showMoreTreatments = (hospital) => {
      console.log('시술 정보:', hospital.treatments);
      
      // 원본 URL 생성 가능 여부 확인
      if (canGenerateOriginalUrl(hospital)) {
        // 원본 URL로 새 창 열기
        const url = generateOriginalUrl(hospital);
        if (url) {
          window.open(url, '_blank');
        }
      } else {
        // URL을 생성할 수 없는 경우 모달이나 다른 방식으로 정보 표시 가능
        console.log('원본 URL을 생성할 수 없습니다.');
        
        // 시술 정보만 나열
        const treatmentInfo = hospital.treatments.map(t => ({
          name: t.treatment_groups_name,
          normal_price: formatPrice(t.min_normal_price, t.max_normal_price),
          event_price: hasEventPrice(t) ? formatPrice(t.min_event_price, t.max_event_price) : '없음',
          views: t.view_count || 0,
          submits: t.submit_count || 0
        }));
        
        console.table(treatmentInfo);
      }
    };

    // 별점 변환 (10점 만점 -> 5점 만점)
    const getStarRating = (score) => {
      if (!score || isNaN(Number(score))) return 0;
      
      // 10점 만점 시스템에서 5점 만점으로 변환
      const normalizedScore = Number(score) / 2;
      
      // 소수점 첫째 자리까지만 유지 (0.5 단위로)
      return Math.round(normalizedScore * 2) / 2;
    };

    // 소스별 색상 지정
    const getSourceColor = (source) => {
      const colorMap = {
        'modoodoc': 'green',
        'docple': 'blue',
        'mediserve': 'purple',
        'goodoc': 'orange'
      };
      
      return colorMap[source] || 'default';
    };
    
    // 소스 이름 포맷팅
    const formatSourceName = (source) => {
      if (!source) return '-';
      
      const nameMap = {
        'modoodoc': '모두닥',
        'docple': '닥플',
        'mediserve': '메디서브',
        'goodoc': '굿닥'
      };
      
      return nameMap[source] || source;
    };

    // 행 번호 계산
    const getRowIndex = (index) => {
      return (currentPage.value - 1) * pageSize.value + index + 1;
    };

    // 이벤트 핸들러 - 필터 값 변경 처리
    const onSourceChange = (value) => {
      console.log('소스 변경:', value);
      filters.source = value;
      // 값 변경 시 자동 검색 실행
      if (debug.value) console.log('소스 필터 변경 -> 자동 검색');
      debouncedSearch(300);
    };
    
    const onCityChange = (value) => {
      console.log('지역 변경:', value);
      filters.city = value;
      // 값 변경 시 자동 검색 실행
      if (debug.value) console.log('지역 필터 변경 -> 자동 검색');
      debouncedSearch(300);
    };
    
    const onTreatmentChange = (value) => {
      console.log('치료 항목 변경:', value);
      filters.treatmentId = value;
      // 값 변경 시 자동 검색 실행
      if (debug.value) console.log('치료 항목 필터 변경 -> 자동 검색');
      debouncedSearch(300);
    };
    
    const onKeywordChange = (value) => {
      console.log('키워드 변경:', value);
      filters.keyword = value;
      // 키워드는 타이핑 중에 자동 검색이 발생하지 않도록 조금 더 긴 지연 설정
      if (debug.value) console.log('키워드 필터 변경 -> 디바운스 검색');
      debouncedSearch(500);
    };

    // 시술의 진료과목 정보 추출
    const getTreatmentDepartment = (treatment) => {
      if (treatment.treatment_group_s && 
          treatment.treatment_group_s.group && 
          treatment.treatment_group_s.group.group && 
          treatment.treatment_group_s.group.group.name) {
        return treatment.treatment_group_s.group.group.name;
      }
      return '-';
    };
    
    // 시술의 카테고리 정보 추출
    const getTreatmentCategory = (treatment) => {
      if (treatment.treatment_group_s && 
          treatment.treatment_group_s.group && 
          treatment.treatment_group_s.group.name) {
        return treatment.treatment_group_s.group.name;
      }
      return '-';
    };

    // 컴포넌트 마운트 시 데이터 조회
    onMounted(() => {
      fetchHospitals();
    });

    return {
      hospitals,
      loading,
      columns,
      pagination,
      filters,
      pageSize,
      totalItems,
      fallbackImg,
      debug,
      fetchHospitals,
      resetFilters,
      handlePageSizeChange,
      handleTableChange,
      getHospitalImageUrl,
      hasTreatments,
      getTreatments,
      hasEventPrice,
      formatPrice,
      addCommas,
      showMoreTreatments,
      getStarRating,
      generateOriginalUrl,
      canGenerateOriginalUrl,
      getSourceColor,
      formatSourceName,
      getRowIndex,
      onSourceChange,
      onCityChange,
      onTreatmentChange,
      onKeywordChange,
      getTreatmentDepartment,
      getTreatmentCategory
    };
  }
});
</script>

<style scoped>
.hospital-management {
  min-height: 100vh;
  background-color: #f7f7f7;
  padding: 60px;
}

.data-table-card {
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.image-cell-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 120px;
}

.hospital-image {
  border-radius: 4px;
  overflow: hidden;
  display: block;
  margin: 0 auto;
  cursor: zoom-in;
}

:deep(.ant-image-mask) {
  border-radius: 4px;
}

/* 이미지 갤러리를 더 세련되게 보이도록 스타일 추가 */
:deep(.ant-image-preview-img) {
  border-radius: 2px;
}

:deep(.ant-image-preview-operations) {
  background-color: rgba(0, 0, 0, 0.7);
}

.cell-content {
  display: block;
  word-break: break-all;
  word-wrap: break-word;
}

.treatments-container {
  padding: 0 8px;
}

.treatment-item {
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 8px;
  background-color: #fafafa;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.treatment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.treatment-name {
  font-weight: 500;
  color: #333;
  font-size: 13px;
}

.treatment-counts {
  font-size: 12px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 8px;
}

.count-item {
  cursor: help;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.treatment-prices {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.treatment-image {
  display: flex;
  justify-content: center;
  margin-top: 4px;
}

:deep(.ant-tag) {
  margin-right: 0;
}

:deep(.ant-table-thead > tr > th) {
  background-color: #f5f5f5;
  font-weight: 500;
  padding: 12px 8px;
  text-align: center;
}

:deep(.ant-table-tbody > tr > td) {
  padding: 12px 8px;
  text-align: center;
}

:deep(.ant-empty-image) {
  height: 40px !important;
}

:deep(.ant-empty-description) {
  font-size: 12px;
  color: #999;
}

.no-link {
  color: #999;
  font-size: 12px;
  padding: 0 6px;
}

.row-number {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 28px;
  height: 28px;
  line-height: 1;
  border-radius: 50%;
  background-color: #f0f5ff;
  color: #1890ff;
  font-weight: 500;
  font-size: 13px;
  text-align: center;
  margin: 0 auto;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.treatment-category {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 4px;
}
</style> 